import React, { useCallback, useState } from "react";
import styles from "./FileDropzone.module.scss";
import classnames from "classnames";

export type FileDropzoneMode = "string" | "arrayBuffer" | "dataUrl";

type FileDropzoneResult<T extends FileDropzoneMode> = T extends "string" ? string : T extends "arrayBuffer" ? ArrayBuffer : string;

interface FileDropzoneProps<T extends FileDropzoneMode> {
  mode: T;
  accept?: string;
  onFile: (file: FileDropzoneResult<T>, fileItem: File) => void;
  className?: string;
  dragOverClassName?: string;
}

export const FileDropzone = <T extends FileDropzoneMode>({
  mode,
  accept,
  onFile,
  className,
  dragOverClassName,
  children,
}: React.PropsWithChildren<FileDropzoneProps<T>>) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const getFileContent = useCallback(
    (file: File) =>
      new Promise<FileDropzoneResult<T>>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve(e.target?.result as FileDropzoneResult<T>);
        };
        reader.onerror = (e) => {
          reject(e);
        };
        if (mode === "string") reader.readAsText(file);
        else if (mode === "arrayBuffer") reader.readAsArrayBuffer(file);
        else if (mode === "dataUrl") reader.readAsDataURL(file);
      }),
    [mode]
  );
  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(true);
    },
    [setIsDragOver]
  );
  const onDragLeave = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
    },
    [setIsDragOver]
  );
  const onDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      // A drag does not have to carry a file. Dropping selected text, a link, or an image dragged
      // from another page all arrive here with an EMPTY `files`, and reading `files[0].name` threw
      // a TypeError out of the handler -- so a stray drop anywhere on the zone left the dialog
      // looking dead, with the error only in the console.
      const file = e.dataTransfer.files?.[0];
      if (!file) return;
      // Extension match, not a mime check: `accept` is the same comma-separated list the file
      // picker takes. A file that does not match is ignored exactly as before.
      if (accept && !accept.split(",").some((ext) => file.name.endsWith(ext.trim()))) return;
      onFile(await getFileContent(file), file);
    },
    [setIsDragOver, getFileContent, onFile, accept]
  );
  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const input = document.createElement("input");
      input.type = "file";
      if (accept) input.accept = accept;
      input.addEventListener("change", async (e: any) => {
        // Same guard as the drop path: `files` is empty if the picker closes without a selection.
        const file = e.target.files?.[0];
        if (!file) return;
        onFile(await getFileContent(file), file);
      });
      input.click();
    },
    [getFileContent, onFile, accept]
  );

  return (
    <div
      className={classnames(styles.dropzone, className, isDragOver && styles.isDragOver, isDragOver && dragOverClassName)}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={onClick}
      data-testid={"file-dropzone"}
    >
      {children}
    </div>
  );
};
